import type { ReactNode } from 'react';

/** What a screen is telling the operator. */
export type StatusTone = 'success' | 'error' | 'warning' | 'info' | 'loading';

/**
 * Tone classes, matching `apps/portal/src/components/StatusBanner.tsx`. `info` is the one tone
 * the portal does not carry: the ops tool has neutral outcomes ("preview wrote nothing") that are
 * neither a success nor a warning, and colouring those green would make a dry run look like a send.
 */
const TONE_CLASSES: Record<StatusTone, string> = {
  success: 'bg-primary-50 text-primary-900 border-primary-200',
  error: 'bg-red-50 text-red-900 border-red-200',
  warning: 'bg-amber-50 text-amber-900 border-amber-200',
  info: 'bg-gray-50 text-gray-800 border-gray-200',
  loading: 'bg-gray-50 text-gray-700 border-gray-200',
};

const TONE_GLYPH: Record<Exclude<StatusTone, 'loading'>, string> = {
  success: '✓',
  error: '!',
  warning: '!',
  info: 'i',
};

/**
 * The one status line a screen shows.
 *
 * `role="status"` with a polite live region, so an outcome an operator triggered is announced
 * rather than only painted.
 */
export function StatusBanner({
  tone,
  children,
}: {
  readonly tone: StatusTone;
  readonly children: ReactNode;
}) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={`flex items-start gap-3 rounded-lg border px-4 py-3 text-sm ${TONE_CLASSES[tone]}`}
    >
      {tone === 'loading' ? (
        <span
          aria-hidden="true"
          className="mt-0.5 h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600 motion-reduce:animate-none"
        />
      ) : (
        <span aria-hidden="true" className="shrink-0 font-semibold">
          {TONE_GLYPH[tone]}
        </span>
      )}
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
